import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

const BASE = "/api/product/stock-adjustments/";

export const fetchStockAdjustments = createAsyncThunk(
  "stockAdjustments/fetchStockAdjustments",
  async ({ search = "", status = "", store = "" } = {}) => {
    const qp = new URLSearchParams();
    if (String(search).trim()) qp.set("search", String(search).trim());
    if (status) qp.set("status", status);
    if (store) qp.set("items__store", String(store));
    qp.set("ordering", "-created_at");
    const data = await apiFetch(`${BASE}?${qp.toString()}`, { method: "GET" });
    return Array.isArray(data) ? data : data?.results || [];
  }
);

export const fetchStockAdjustmentDetail = createAsyncThunk(
  "stockAdjustments/fetchStockAdjustmentDetail",
  async ({ adjustmentId }) => apiFetch(`${BASE}${adjustmentId}/`, { method: "GET" })
);

function errorPayload(err, fallback) {
  return err?.data || { detail: err?.message || fallback };
}

/** Создаёт (без id) или целиком заменяет черновик (с id). */
export const saveStockAdjustment = createAsyncThunk(
  "stockAdjustments/saveStockAdjustment",
  async ({ adjustmentId = null, reason, comment, items }, { rejectWithValue }) => {
    const body = { reason, comment, items };
    try {
      return adjustmentId
        ? await apiFetch(`${BASE}${adjustmentId}/`, { method: "PUT", body })
        : await apiFetch(BASE, { method: "POST", body });
    } catch (err) {
      return rejectWithValue(errorPayload(err, "Не удалось сохранить корректировку"));
    }
  }
);

export const postStockAdjustment = createAsyncThunk(
  "stockAdjustments/postStockAdjustment",
  async ({ adjustmentId }, { rejectWithValue }) => {
    try {
      return await apiFetch(`${BASE}${adjustmentId}/post/`, { method: "POST" });
    } catch (err) {
      return rejectWithValue(errorPayload(err, "Не удалось провести корректировку"));
    }
  }
);

export const cancelStockAdjustment = createAsyncThunk(
  "stockAdjustments/cancelStockAdjustment",
  async ({ adjustmentId }, { rejectWithValue }) => {
    try {
      return await apiFetch(`${BASE}${adjustmentId}/cancel/`, { method: "POST" });
    } catch (err) {
      return rejectWithValue(errorPayload(err, "Не удалось отменить корректировку"));
    }
  }
);

export const deleteStockAdjustment = createAsyncThunk(
  "stockAdjustments/deleteStockAdjustment",
  async ({ adjustmentId }, { rejectWithValue }) => {
    try {
      await apiFetch(`${BASE}${adjustmentId}/`, { method: "DELETE" });
      return adjustmentId;
    } catch (err) {
      return rejectWithValue(errorPayload(err, "Не удалось удалить корректировку"));
    }
  }
);

const stockAdjustmentsSlice = createSlice({
  name: "stockAdjustments",
  initialState: {
    items: [],
    status: "idle",
    error: null,
    detail: null,
    detailStatus: "idle",
    detailError: null,
  },
  reducers: {
    clearDetail(state) {
      state.detail = null;
      state.detailStatus = "idle";
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockAdjustments.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchStockAdjustments.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchStockAdjustments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Не удалось загрузить корректировки";
      })
      .addCase(fetchStockAdjustmentDetail.pending, (state) => {
        state.detailStatus = "loading";
        state.detailError = null;
      })
      .addCase(fetchStockAdjustmentDetail.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = action.payload;
      })
      .addCase(fetchStockAdjustmentDetail.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.detailError = action.error?.message || "Не удалось загрузить документ";
      })
      .addCase(saveStockAdjustment.fulfilled, (state, action) => {
        state.detail = action.payload;
      })
      .addCase(postStockAdjustment.fulfilled, (state, action) => {
        state.detail = action.payload;
      })
      .addCase(cancelStockAdjustment.fulfilled, (state, action) => {
        state.detail = action.payload;
      });
  },
});

export const { clearDetail } = stockAdjustmentsSlice.actions;

export const selectStockAdjustments = (state) => state.stockAdjustments.items;
export const selectStockAdjustmentsStatus = (state) => state.stockAdjustments.status;
export const selectStockAdjustmentsError = (state) => state.stockAdjustments.error;
export const selectStockAdjustmentDetail = (state) => state.stockAdjustments.detail;
export const selectStockAdjustmentDetailStatus = (state) => state.stockAdjustments.detailStatus;
export const selectStockAdjustmentDetailError = (state) => state.stockAdjustments.detailError;

export default stockAdjustmentsSlice.reducer;
